/**
 * ScoringWidget — Dynamic revenue-maximizing widget powered by scoring-next
 *
 * Replaces the static FlowChatWidget with a scoring engine that dynamically
 * selects the highest-revenue vertical per session. Shows ONE action at a time:
 * profiler -> vertical CTA cards -> session summary.
 *
 * Usage:
 *   <ScoringWidget chatbotId="abc-123" />
 *   <ScoringWidget embedded entryIntent={{ primary_vertical: 'auto_insurance', source: 'landing', confidence: 0.9 }} />
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot,
  Check,
  ChevronRight,
  ExternalLink,
  Loader2,
  Mail,
  Phone,
  Send,
  X,
} from 'lucide-react';
import ProfileBuilder from '@/components/scoring/ProfileBuilder';
import { supabasePost } from '@/utils/supabaseRest';
import { BehaviorTracker } from '@/lib/BehaviorTracker';

// ─── Types ────────────────────────────────────────────────

type WidgetState = 'profiling' | 'email_capture' | 'presenting' | 'loading' | 'complete';
type Decision = 'CALL' | 'LEAD' | 'MULTI_OFFER' | null;

interface ScoreUserResponse {
  intent_score: number;
  monetization_score: number;
  intent_breakdown: Record<string, number>;
  monetization_breakdown: Record<string, number>;
  decision: 'CALL' | 'LEAD' | 'MULTI_OFFER';
  route_to: { route_type: string; route_url?: string; route_meta?: Record<string, unknown>; vertical_id?: string; vertical_name?: string } | null;
  offers: Array<{ vertical_id: string; vertical_name: string; cluster: string; route_type: string; route_url: string; route_meta: Record<string, unknown>; estimated_revenue: number }> | null;
  reason: string;
  latency_ms: number;
}

interface ScoringWidgetProps {
  chatbotId?: string;
  entryIntent?: { primary_vertical: string; source: string; confidence: number };
  position?: 'bottom-right' | 'bottom-left';
  initialOpen?: boolean;
  embedded?: boolean;
  brandName?: string;        // default: 'IntentDrive'
  brandColor?: string;       // default: '#10b981' (emerald-500)
  brandLogo?: string;        // URL to a logo image
  brandTagline?: string;     // shown under the brand name
}

interface VerticalRoute {
  vertical: string;
  cluster?: string;
  route_type: 'call' | 'lead' | 'redirect';
  route_url: string;
  route_meta: Record<string, unknown>;
  estimated_revenue: number;
  has_more: boolean;
}

interface ConvertedVertical {
  vertical: string;
  revenue: number;
}

interface LeadFormData {
  full_name: string;
  email: string;
  phone: string;
}

// ─── Cluster descriptions ────────────────────────────────

const CLUSTER_DESCRIPTIONS: Record<string, string> = {
  auto_insurance: 'Save on auto insurance with personalized rates',
  home_insurance: 'Protect your home with the best coverage',
  health_insurance: 'Find affordable health insurance plans',
  life_insurance: 'Secure your family with life insurance',
  debt_relief: 'Reduce your debt by 40-60%',
  debt_consolidation: 'Consolidate your payments into one low rate',
  personal_loan: 'Get a personal loan with competitive rates',
  solar: 'Save on energy with solar panel installation',
  home_warranty: 'Cover unexpected home repair costs',
  medicare: 'Find the right Medicare plan for you',
  tax_relief: 'Resolve your tax debt for less than you owe',
  home_security: 'Protect your home with smart security',
  legal: 'Connect with a qualified attorney near you',
};

function getVerticalDescription(vertical: string, cluster?: string): string {
  const key = cluster || vertical;
  return (
    CLUSTER_DESCRIPTIONS[key] ||
    CLUSTER_DESCRIPTIONS[vertical] ||
    `Exclusive offer: ${vertical.replace(/_/g, ' ')}`
  );
}

function formatVerticalName(vertical: string): string {
  return vertical
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ─── API Helpers ──────────────────────────────────────────

const SUPABASE_URL =
  import.meta.env?.VITE_SUPABASE_URL || 'https://khwvlnkrtrwvfbnnjsrc.supabase.co';
const SUPABASE_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY || '';

async function apiFetch<T = unknown>(
  path: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const url = `${SUPABASE_URL}/functions/v1/${path}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${SUPABASE_KEY}`,
      apikey: SUPABASE_KEY,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

// ─── Sub-components ───────────────────────────────────────

/** CTA card for route_type='call' */
function CallCard({
  route,
  onConvert,
  onReject,
  brandColor = '#10b981',
}: {
  route: VerticalRoute;
  onConvert: () => void;
  onReject: () => void;
  brandColor?: string;
}) {
  const phoneNumber = route.route_url || (route.route_meta?.phone as string) || '';
  return (
    <div className="space-y-4">
      <div className="text-center space-y-1">
        <h3 className="text-lg font-semibold text-white">
          {formatVerticalName(route.vertical)}
        </h3>
        <p className="text-sm text-slate-400">
          {getVerticalDescription(route.vertical, route.cluster)}
        </p>
      </div>
      <p className="text-xs text-emerald-400/80 text-center">A licensed specialist is ready to help you save</p>
      <a
        href={`tel:${phoneNumber.replace(/[^+\d]/g, '')}`}
        onClick={onConvert}
        className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all hover:brightness-110 active:scale-[0.98]"
        style={{ backgroundColor: brandColor }}
      >
        <Phone className="h-4 w-4 shrink-0" />
        <span className="truncate">Call Now — Free Quote</span>
      </a>
      <p className="text-[9px] text-slate-500 text-center">
        By calling, you consent to be connected with a licensed agent. Standard call rates apply.
      </p>
      <button
        onClick={onReject}
        className="w-full text-center text-[10px] text-slate-600 hover:text-slate-400 transition-colors"
      >
        No thanks, show other options
      </button>
    </div>
  );
}

/** CTA card for route_type='redirect' */
function RedirectCard({
  route,
  onConvert,
  onReject,
  brandColor = '#10b981',
}: {
  route: VerticalRoute;
  onConvert: () => void;
  onReject: () => void;
  brandColor?: string;
}) {
  const handleClick = () => {
    onConvert();
    setTimeout(() => {
      window.open(route.route_url, '_blank', 'noopener');
    }, 150);
  };
  return (
    <div className="space-y-4">
      <div className="text-center space-y-1">
        <h3 className="text-lg font-semibold text-white">
          {formatVerticalName(route.vertical)}
        </h3>
        <p className="text-sm text-slate-400">
          {getVerticalDescription(route.vertical, route.cluster)}
        </p>
      </div>
      <button
        onClick={handleClick}
        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white font-semibold text-sm transition-colors"
        style={{ backgroundColor: brandColor }}
      >
        <ExternalLink className="h-4 w-4 shrink-0" />
        <span className="truncate">Get Your Quote</span>
      </button>
      <button
        onClick={onReject}
        className="w-full text-center text-xs text-slate-500 hover:text-slate-300 transition-colors"
      >
        Not interested <ChevronRight className="inline h-3 w-3" />
      </button>
    </div>
  );
}

/** CTA card for route_type='lead' — inline 3-field form */
function LeadCard({
  route,
  sessionId,
  onConvert,
  onReject,
  brandColor = '#10b981',
}: {
  route: VerticalRoute;
  sessionId: string;
  onConvert: () => void;
  onReject: () => void;
  brandColor?: string;
}) {
  const [form, setForm] = useState<LeadFormData>({ full_name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Please fill in all fields');
      return;
    }
    setError(null);
    setSubmitting(true);

    // Button-tied consent: submitting the form = consent granted
    // (consent language is displayed under the submit button)
    if (form.phone.trim()) {
      await recordSmsConsent(form.phone.trim(), sessionId, true);
    }

    try {
      // POST lead data to the vertical's route_url
      await fetch(route.route_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
          session_id: sessionId,
          vertical: route.vertical,
        }),
      });
    } catch {
      // Lead post failure is non-blocking — still count the conversion
    }
    setSubmitting(false);
    onConvert();
  };

  return (
    <div className="space-y-4">
      <div className="text-center space-y-1">
        <h3 className="text-lg font-semibold text-white">
          {formatVerticalName(route.vertical)}
        </h3>
        <p className="text-sm text-slate-400">
          {getVerticalDescription(route.vertical, route.cluster)}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2.5">
        <input
          name="full_name"
          type="text"
          placeholder="Full Name"
          value={form.full_name}
          onChange={handleChange}
          className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
        />
        <input
          name="email"
          type="email"
          placeholder="Email"
          value={form.email}
          onChange={handleChange}
          className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
        />
        <input
          name="phone"
          type="tel"
          placeholder="Phone"
          value={form.phone}
          onChange={handleChange}
          className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
        />
        {error && <p className="text-xs text-red-400">{error}</p>}
        {/* ACTION BUTTON — consent tied to tapping */}
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-50 hover:brightness-110 active:scale-[0.98]"
          style={{ backgroundColor: brandColor }}
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          <span className="truncate">{submitting ? 'Connecting you...' : 'Get My Quote'}</span>
        </button>
        {/* CONSENT LANGUAGE — under button */}
        <p className="text-[9px] text-slate-500 leading-tight text-center">
          By submitting, you agree to be contacted by call, text, or email by licensed partners. Msg & data rates may apply. Reply STOP to cancel. Consent not required for purchase.{' '}
          <a href="/privacy" target="_blank" className="underline hover:text-slate-300">Privacy</a>
        </p>
      </form>
      <button
        onClick={onReject}
        className="w-full text-center text-[10px] text-slate-600 hover:text-slate-400 transition-colors"
      >
        No thanks, skip this
      </button>
    </div>
  );
}

/** Email capture step shown after profiling, before first vertical */
/** Record SMS consent to the database */
async function recordSmsConsent(phone: string, sessionId: string, consentGiven: boolean) {
  if (!consentGiven || !phone.trim()) return;
  try {
    await fetch(`${SUPABASE_URL}/rest/v1/sms_consents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_KEY,
        'Authorization': `Bearer ${SUPABASE_KEY}`,
        'Prefer': 'return=minimal',
      },
      body: JSON.stringify({
        phone: phone.replace(/[^\d+]/g, ''),
        consent_given: true,
        consent_text: 'I agree to receive text messages about savings and offers. Message frequency varies. Msg & data rates may apply. Reply STOP to cancel.',
        consent_source: 'widget',
        consent_page_url: window.location.href,
        consent_ip: null, // captured server-side
        consent_user_agent: navigator.userAgent,
        session_id: sessionId,
        status: 'active',
      }),
    });
  } catch {
    // Non-blocking — consent recording failure shouldn't stop the flow
  }
}

function EmailCaptureStep({
  onSubmit,
  onSkip,
  sessionId,
  brandColor = '#10b981',
}: {
  onSubmit: (email: string, phone?: string) => void;
  onSkip: () => void;
  sessionId?: string;
  brandColor?: string;
}) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);

    // Button-tied consent: tapping "See My Matches" = consent granted
    // Consent language is displayed under the button (legally tied to action)
    if (phone.trim() && sessionId) {
      await recordSmsConsent(phone.trim(), sessionId, true);
    }

    await onSubmit(email.trim(), phone.trim() || undefined);
    setSubmitting(false);
  };

  return (
    <div className="flex flex-col items-center justify-center h-full space-y-5">
      <div className="text-center space-y-2">
        <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center" style={{ background: `${brandColor}22` }}>
          <Mail className="h-6 w-6" style={{ color: brandColor }} />
        </div>
        <h3 className="text-lg font-semibold text-white">
          We found options that could save you money
        </h3>
        <p className="text-xs text-slate-400">Enter your details to see your personalized matches</p>
      </div>
      <form onSubmit={handleSubmit} className="w-full space-y-3">
        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
          autoFocus
        />
        <input
          type="tel"
          placeholder="Phone number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
        />
        {/* ACTION BUTTON — consent is tied to tapping this */}
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-50 hover:brightness-110 active:scale-[0.98]"
          style={{ backgroundColor: brandColor }}
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          {submitting ? 'Finding matches...' : 'See My Matches'}
        </button>
        {/* CONSENT LANGUAGE — under the button, tied to the action */}
        <p className="text-[9px] text-slate-500 leading-tight text-center px-2">
          By tapping above, you agree to be contacted by call, text, or email by licensed partners regarding your inquiry. Msg frequency varies. Msg & data rates may apply. Reply STOP to cancel. Consent not required for purchase.{' '}
          <a href="/privacy" target="_blank" className="underline hover:text-slate-300">Privacy Policy</a> &middot;{' '}
          <a href="/terms" target="_blank" className="underline hover:text-slate-300">Terms</a>
        </p>
      </form>
      <button
        onClick={onSkip}
        className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors"
      >
        No thanks, just browsing
      </button>
    </div>
  );
}

/** Session summary shown when all verticals are exhausted */
function SessionSummary({ conversions, onStartOver }: { conversions: ConvertedVertical[]; onStartOver?: () => void }) {
  const totalValue = conversions.reduce((sum, c) => sum + c.revenue, 0);
  return (
    <div className="space-y-4 py-2">
      <div className="text-center space-y-1">
        <h3 className="text-lg font-semibold text-white">
          You've been matched with {conversions.length} offer{conversions.length !== 1 ? 's' : ''}
        </h3>
        {totalValue > 0 && (
          <p className="text-sm text-emerald-400">
            Session value: ${totalValue.toFixed(2)}
          </p>
        )}
      </div>
      {conversions.length > 0 && (
        <ul className="space-y-2">
          {conversions.map((c, i) => (
            <li
              key={i}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700"
            >
              <Check className="h-4 w-4 text-emerald-400 shrink-0" />
              <span className="text-sm text-slate-200">
                {formatVerticalName(c.vertical)}
              </span>
              {c.revenue > 0 && (
                <span className="ml-auto text-xs text-slate-500">
                  ${c.revenue.toFixed(2)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {conversions.length === 0 && (
        <p className="text-sm text-slate-500 text-center">
          Thanks for your time. Check back soon for new offers.
        </p>
      )}
      {onStartOver && (
        <button
          onClick={onStartOver}
          className="w-full mt-4 py-2.5 rounded-xl border border-slate-700 bg-slate-800 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
        >
          Start Over
        </button>
      )}
    </div>
  );
}

// ─── Slide animation variants ─────────────────────────────

const slideVariants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

// ─── Main Component ───────────────────────────────────────

export function ScoringWidget({
  chatbotId,
  entryIntent,
  position = 'bottom-right',
  initialOpen = false,
  embedded = false,
  brandName = 'IntentDrive',
  brandColor = '#10b981',
  brandLogo,
  brandTagline,
}: ScoringWidgetProps) {
  const [isOpen, setIsOpen] = useState(initialOpen || embedded);
  const [widgetState, setWidgetState] = useState<WidgetState>('profiling');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentRoute, setCurrentRoute] = useState<VerticalRoute | null>(null);
  const [conversions, setConversions] = useState<ConvertedVertical[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [capturedEmail, setCapturedEmail] = useState<string | null>(null);
  const [currentDecision, setCurrentDecision] = useState<Decision>(null);
  const [scores, setScores] = useState<{ intent: number; monetization: number } | null>(null);
  const pendingFirstRouteRef = useRef<VerticalRoute | null>(null);
  const initialized = useRef(false);

  // ─── Behavioral tracking (V2 dual-score engine) ─────────────
  const behaviorTrackerRef = useRef<BehaviorTracker | null>(null);
  const profileDataRef = useRef<Record<string, unknown> | null>(null);

  // ─── Engagement tracking (refs to avoid re-renders) ──────────
  const sessionStartRef = useRef<number>(Date.now());
  const verticalShownAtRef = useRef<number>(Date.now());
  const clickCountRef = useRef<number>(0);

  /** Derive engagement level from time spent on the current vertical */
  function getEngagementLevel(timeMs: number): 'low' | 'medium' | 'high' {
    if (timeMs < 3000) return 'low';
    if (timeMs <= 15000) return 'medium';
    return 'high';
  }

  // Create session on first open
  useEffect(() => {
    if (!isOpen || initialized.current) return;
    initialized.current = true;
    createSession();
  }, [isOpen]);

  const createSession = async () => {
    if (!chatbotId) return;
    try {
      const res = await apiFetch<{ session_id: string }>('session-start', {
        chatbot_id: chatbotId,
      });
      setSessionId(res.session_id);

      // Start behavioral tracking for V2 dual-score engine
      if (behaviorTrackerRef.current) behaviorTrackerRef.current.destroy();
      const tracker = new BehaviorTracker(res.session_id, SUPABASE_URL, SUPABASE_KEY);
      tracker.start();
      behaviorTrackerRef.current = tracker;
    } catch (err: any) {
      console.error('Failed to create session:', err);
      setError('Unable to start session. Please try again.');
    }
  };

  // Cleanup BehaviorTracker on unmount
  useEffect(() => {
    return () => {
      if (behaviorTrackerRef.current) {
        behaviorTrackerRef.current.destroy();
        behaviorTrackerRef.current = null;
      }
    };
  }, []);

  // Called when ProfileBuilder finishes — run V2 dual scoring then decide path
  const handleProfileComplete = useCallback(
    async (firstRoute: VerticalRoute) => {
      // Track form completion
      behaviorTrackerRef.current?.trackFormComplete();

      // Stash the first route from scoring-next (used as fallback for MULTI_OFFER)
      pendingFirstRouteRef.current = firstRoute;

      // ── V2: Call score-user for dual scores + decision ──
      if (sessionId && behaviorTrackerRef.current) {
        try {
          const behaviorSnapshot = behaviorTrackerRef.current.getSnapshot();
          const profile = profileDataRef.current || {};

          const scoreRes = await apiFetch<ScoreUserResponse>('score-user', {
            session_id: sessionId,
            user_data: { ...profile, primary_vertical: entryIntent?.primary_vertical },
            behavior_data: behaviorSnapshot,
            source_data: {
              referrer: document.referrer || undefined,
              landing_page_url: window.location.href,
              utm_source: new URLSearchParams(window.location.search).get('utm_source') || undefined,
              utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
              utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined,
              affiliate_id: new URLSearchParams(window.location.search).get('source') || undefined,
              sub_id: new URLSearchParams(window.location.search).get('sub_id') || undefined,
            },
          });

          setScores({ intent: scoreRes.intent_score, monetization: scoreRes.monetization_score });
          setCurrentDecision(scoreRes.decision);

          // Decision-based routing
          if (scoreRes.decision === 'CALL' && scoreRes.route_to?.route_url) {
            // HIGH INTENT: Skip email capture, show call card immediately
            const callRoute: VerticalRoute = {
              vertical: scoreRes.route_to.vertical_name || scoreRes.route_to.vertical_id || 'call',
              cluster: undefined,
              route_type: 'call',
              route_url: scoreRes.route_to.route_url,
              route_meta: scoreRes.route_to.route_meta || {},
              estimated_revenue: 0,
              has_more: true,
            };
            verticalShownAtRef.current = Date.now();
            clickCountRef.current = 0;
            setCurrentRoute(callRoute);
            setWidgetState('presenting');
            return;
          }

          if (scoreRes.decision === 'LEAD' && scoreRes.route_to?.route_url) {
            // MID INTENT: Show email capture then lead form
            // Override the pending route with the lead-specific one
            pendingFirstRouteRef.current = {
              vertical: scoreRes.route_to.vertical_name || scoreRes.route_to.vertical_id || 'lead',
              cluster: undefined,
              route_type: 'lead',
              route_url: scoreRes.route_to.route_url,
              route_meta: scoreRes.route_to.route_meta || {},
              estimated_revenue: 0,
              has_more: true,
            };
            setWidgetState('email_capture');
            return;
          }

          // MULTI_OFFER or fallback: use the scoring-next first route
          // Fall through to email capture then vertical rotation
        } catch (err) {
          console.warn('score-user call failed, falling back to scoring-next flow:', err);
          // Non-fatal — fall back to existing behavior
        }
      }

      // Default path: email capture → first vertical from scoring-next
      setWidgetState('email_capture');
    },
    [sessionId, entryIntent],
  );

  // Email capture handlers
  const handleEmailSubmit = async (email: string) => {
    setCapturedEmail(email);
    // Save partial lead to Supabase
    if (sessionId && chatbotId) {
      try {
        await supabasePost('leads', {
          email,
          session_id: sessionId,
          chatbot_id: chatbotId,
          status: 'partial',
          source: 'email_capture',
        });
      } catch (err) {
        console.error('Failed to save email capture lead:', err);
      }
    }
    proceedToFirstVertical();
  };

  const handleEmailSkip = () => {
    proceedToFirstVertical();
  };

  const proceedToFirstVertical = () => {
    const firstRoute = pendingFirstRouteRef.current;
    pendingFirstRouteRef.current = null;
    // If scoring-next returned { vertical: null } (no matching verticals),
    // skip straight to the complete screen instead of crashing.
    if (!firstRoute || !firstRoute.vertical) {
      setCurrentRoute(null);
      setWidgetState('complete');
      return;
    }
    verticalShownAtRef.current = Date.now();
    clickCountRef.current = 0;
    setCurrentRoute(firstRoute);
    setWidgetState('presenting');
  };

  // Call scoring-next with convert or reject, then show next or finish
  const advanceScoring = async (action: 'convert' | 'reject', revenue?: number) => {
    if (!sessionId) return;

    // ── Capture engagement metrics before advancing ──
    const now = Date.now();
    const timeOnVerticalMs = now - verticalShownAtRef.current;
    const totalSessionMs = now - sessionStartRef.current;
    const engagementLevel = getEngagementLevel(timeOnVerticalMs);

    setWidgetState('loading');
    try {
      const body: Record<string, unknown> = {
        session_id: sessionId,
        action,
        engagement_level: engagementLevel,
        metadata: {
          time_on_vertical_ms: timeOnVerticalMs,
          total_session_ms: totalSessionMs,
          click_count: clickCountRef.current,
          ...(capturedEmail ? { email: capturedEmail } : {}),
        },
      };
      if (action === 'convert' && revenue != null) {
        body.revenue = revenue;
      }
      const res = await apiFetch<VerticalRoute & { has_more: boolean }>('scoring-next', body);

      if (res.has_more === false && !res.vertical) {
        // No more verticals
        setCurrentRoute(null);
        setWidgetState('complete');
      } else {
        // Reset per-vertical engagement trackers for the next vertical
        verticalShownAtRef.current = Date.now();
        clickCountRef.current = 0;
        setCurrentRoute(res);
        setWidgetState('presenting');
      }
    } catch (err: any) {
      console.error('scoring-next error:', err);
      // On error, end the session gracefully
      setCurrentRoute(null);
      setWidgetState('complete');
    }
  };

  const handleConvert = () => {
    if (!currentRoute) return;
    clickCountRef.current += 1;
    const revenue =
      (currentRoute.route_meta?.payout as number) ??
      (currentRoute.route_meta?.revenue as number) ??
      currentRoute.estimated_revenue ??
      0;
    setConversions((prev) => [
      ...prev,
      { vertical: currentRoute.vertical, revenue },
    ]);
    advanceScoring('convert', revenue);
  };

  const handleReject = () => {
    clickCountRef.current += 1;
    advanceScoring('reject');
  };

  const handleStartOver = () => {
    setWidgetState('profiling');
    setCurrentRoute(null);
    setConversions([]);
    setError(null);
    setCapturedEmail(null);
    setCurrentDecision(null);
    setScores(null);
    pendingFirstRouteRef.current = null;
    profileDataRef.current = null;
    sessionStartRef.current = Date.now();
    verticalShownAtRef.current = Date.now();
    clickCountRef.current = 0;
    // Create a fresh session (also restarts BehaviorTracker)
    initialized.current = false;
    createSession();
    initialized.current = true;
  };

  // Position classes
  const positionClasses: Record<string, string> = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
  };

  const WidgetContent = (
    <div
      className={`flex flex-col ${
        embedded ? 'w-full h-full' : 'w-full max-w-[380px] h-[560px]'
      } rounded-2xl border border-slate-700/80 bg-slate-900 shadow-2xl overflow-hidden`}
    >
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center gap-3 shrink-0"
        style={{
          background: `linear-gradient(to right, ${brandColor}, ${brandColor}dd)`,
        }}
      >
        {brandLogo ? (
          <img src={brandLogo} alt={brandName} className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Bot className="h-4 w-4 text-white" />
          </div>
        )}
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">{brandName}</div>
          {brandTagline ? (
            <div className="text-xs text-white/70">{brandTagline}</div>
          ) : (
            <div className="text-xs text-white/70 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
              Online
            </div>
          )}
        </div>
        {!embedded && (
          <button
            onClick={() => setIsOpen(false)}
            className="text-white/70 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-5">
        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400">
            {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* ── Profiling state ── */}
          {widgetState === 'profiling' && sessionId && (
            <motion.div
              key="profiling"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
            >
              <ProfileBuilder
                sessionId={sessionId}
                entryIntent={entryIntent}
                onComplete={handleProfileComplete}
                onProfileData={(data) => { profileDataRef.current = data; }}
                onStepChange={(step) => {
                  behaviorTrackerRef.current?.trackStepChange(step);
                  if (step === 0) behaviorTrackerRef.current?.trackFormStart();
                }}
              />
            </motion.div>
          )}

          {/* ── Email capture state ── */}
          {widgetState === 'email_capture' && (
            <motion.div
              key="email_capture"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
            >
              <EmailCaptureStep
                onSubmit={handleEmailSubmit}
                onSkip={handleEmailSkip}
                sessionId={sessionId || undefined}
                brandColor={brandColor}
              />
            </motion.div>
          )}

          {/* ── Loading state ── */}
          {widgetState === 'loading' && (
            <motion.div
              key="loading"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2 }}
              className="flex flex-col items-center justify-center h-full gap-3"
            >
              <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
              <p className="text-sm text-slate-400">Finding your next best offer...</p>
            </motion.div>
          )}

          {/* ── Presenting state — vertical CTA card ── */}
          {widgetState === 'presenting' && currentRoute && (
            <motion.div
              key={`presenting-${currentRoute.vertical}`}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex flex-col justify-center h-full"
            >
              {currentRoute.route_type === 'call' && (
                <CallCard
                  route={currentRoute}
                  onConvert={handleConvert}
                  onReject={handleReject}
                  brandColor={brandColor}
                />
              )}
              {currentRoute.route_type === 'lead' && sessionId && (
                <LeadCard
                  route={currentRoute}
                  sessionId={sessionId}
                  onConvert={handleConvert}
                  onReject={handleReject}
                  brandColor={brandColor}
                />
              )}
              {currentRoute.route_type === 'redirect' && (
                <RedirectCard
                  route={currentRoute}
                  onConvert={handleConvert}
                  onReject={handleReject}
                  brandColor={brandColor}
                />
              )}
            </motion.div>
          )}

          {/* ── Complete state — session summary ── */}
          {widgetState === 'complete' && (
            <motion.div
              key="complete"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex flex-col justify-center h-full"
            >
              <SessionSummary conversions={conversions} onStartOver={handleStartOver} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer — progress indicator */}
      <div className="shrink-0 px-4 py-2.5 border-t border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {(['profiling', 'presenting', 'complete'] as const).map((step, i) => {
            const stepOrder = ['profiling', 'presenting', 'complete'];
            const resolvedState =
              widgetState === 'loading' ? 'presenting'
              : widgetState === 'email_capture' ? 'profiling'
              : widgetState;
            const currentIdx = stepOrder.indexOf(resolvedState);
            const isActive = i <= currentIdx;
            return (
              <div
                key={step}
                className={`h-1 rounded-full transition-all duration-300 ${
                  isActive ? 'w-8' : 'w-4 bg-slate-700'
                }`}
                style={isActive ? { backgroundColor: brandColor } : undefined}
              />
            );
          })}
        </div>
        <span className="text-[10px] text-slate-600 flex items-center gap-2">
          {scores && (
            <span className="text-[9px] text-emerald-600/60 font-mono">
              I:{scores.intent} M:{scores.monetization}
            </span>
          )}
          Powered by {brandName}
        </span>
      </div>
    </div>
  );

  // Embedded mode: render inline
  if (embedded) return WidgetContent;

  // Floating mode: render with toggle button
  return (
    <div className={`fixed ${positionClasses[position]} z-50`}>
      <AnimatePresence>
        {isOpen ? (
          <motion.div
            key="widget"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {WidgetContent}
          </motion.div>
        ) : (
          <motion.button
            key="button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => setIsOpen(true)}
            className="w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
            style={{ background: `linear-gradient(to bottom right, ${brandColor}, ${brandColor}dd)` }}
          >
            {brandLogo ? (
              <img src={brandLogo} alt={brandName} className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <Bot className="h-6 w-6 text-white" />
            )}
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ScoringWidget;
